import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { loginStart, loginSuccess, loginFailure, logout, initializeAuth } from '../store/slices/authSlice';
import { useLoginMutation } from '../store/api/authApi';
import { userApi } from '../store/api/userApi';
import { LoginCredentials } from '../types/auth';
import { tokenStorage } from '../utils/tokenStorage';
import { useTokenRefresh } from './useTokenRefresh';
import { useEffect } from 'react';

export const useAuth = () => {
  const dispatch = useDispatch();
  const authState = useSelector((state: RootState) => state.auth);
  const [loginMutation, { isLoading }] = useLoginMutation();
  const { refreshToken } = useTokenRefresh();

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch(loginStart());
      
      const result = await loginMutation(credentials).unwrap();
      
      // Create user info from credentials and scope since token is encrypted
      const user = {
        id: credentials.clientId,
        name: credentials.clientId,
        email: '',
        scope: credentials.scope,
        organizationId: credentials.scope === 'support' ? undefined : credentials.clientId,
        organizationName: credentials.clientId,
      };
      
      // Store token data securely (credentials are NOT persisted for security)
      tokenStorage.setTokenData({
        access_token: result.access_token,
        expires_in: result.expires_in,
        token_type: result.token_type,
        scope: result.scope,
        user_data: {
          user,
        }
      });
      
      dispatch(loginSuccess({ user }));

      // Force fresh profile data fetch after login to get current hasClients status
      // This ensures proper onboarding detection for new logins
      dispatch(userApi.util.invalidateTags(['UserProfile']));
      
      return { success: true };
    } catch (error: any) {
      const errorMessage = error?.data?.error_description || error?.message || 'Login failed';
      dispatch(loginFailure(errorMessage));
      return { success: false, error: errorMessage };
    }
  };

  const signOut = () => {
    dispatch(logout());
  };

  const initialize = () => {
    dispatch(initializeAuth());
  };

  // Auto-initialize removed to prevent loops - handled in TokenRefreshProvider

  return {
    ...authState,
    isLoading: isLoading || authState.isLoading,
    login,
    logout: signOut,
    initialize,
    refreshToken,
    // Token utility functions
    getAccessToken: () => tokenStorage.getAccessToken(),
    isTokenValid: () => tokenStorage.isTokenValid(),
    needsRefresh: () => tokenStorage.needsRefresh(),
  };
};

